import React, { useState, useEffect, useRef } from 'react';
import MapFrame from '../../components/MapFrame';
import BlockSubtitle from '../../components/BlockSubtitle';
import MusicPlayer from '../../components/MusicPlayer';
import './Botanica.css';

/* ── Filter map ─────────────────────────────────────────────── */
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;
const src = u => !u ? null : u.startsWith('http') ? u : `${u}`;

/* ── Leaf SVG decorations ───────────────────────────────────── */
const LeafCornerTL = () => (
  <svg className="bot-leaf-corner bot-leaf-corner--tl" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 110 C10 60, 60 10, 110 10" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.4"/>
    <path d="M10 110 Q35 55 80 20 Q60 50 55 90Z" fill="currentColor" opacity="0.18"/>
    <path d="M20 95 Q50 50 90 15" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.3"/>
    <path d="M30 80 Q40 65 55 50 Q45 65 40 85Z" fill="currentColor" opacity="0.15"/>
    <circle cx="60" cy="55" r="2" fill="currentColor" opacity="0.3"/>
    <path d="M55 50 C50 40 45 55 55 50Z" fill="currentColor" opacity="0.2"/>
  </svg>
);

const LeafCornerBR = () => (
  <svg className="bot-leaf-corner bot-leaf-corner--br" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M110 10 C110 60, 60 110, 10 110" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.4"/>
    <path d="M110 10 Q85 65 40 100 Q60 70 65 30Z" fill="currentColor" opacity="0.18"/>
    <path d="M100 25 Q70 70 30 105" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.3"/>
    <path d="M90 40 Q80 55 65 70 Q75 55 80 35Z" fill="currentColor" opacity="0.15"/>
    <circle cx="60" cy="65" r="2" fill="currentColor" opacity="0.3"/>
  </svg>
);

const LeafDivider = () => (
  <svg className="bot-leaf-divider" viewBox="0 0 300 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="0" y1="20" x2="110" y2="20" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
    <path d="M130 20 Q140 8 150 20 Q160 32 170 20" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.7"/>
    <path d="M140 15 Q150 5 158 12" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.5"/>
    <path d="M150 8 L150 20" stroke="currentColor" strokeWidth="1" opacity="0.4"/>
    <path d="M148 20 Q149 14 150 8 Q151 14 152 20Z" fill="currentColor" opacity="0.2"/>
    <line x1="190" y1="20" x2="300" y2="20" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
    <circle cx="150" cy="25" r="2" fill="currentColor" opacity="0.4"/>
  </svg>
);

const SmallLeaf = () => (
  <svg className="bot-small-leaf" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 28 Q6 18 8 8 Q18 6 24 16 Q26 24 16 28Z" fill="currentColor" opacity="0.6"/>
    <path d="M16 28 L16 12" stroke="white" strokeWidth="0.8" opacity="0.5"/>
    <path d="M16 22 Q12 18 10 14" stroke="white" strokeWidth="0.6" opacity="0.4"/>
    <path d="M16 18 Q20 16 22 14" stroke="white" strokeWidth="0.6" opacity="0.4"/>
  </svg>
);

/* ── Countdown ──────────────────────────────────────────────── */
function Countdown({ date, accent }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const ms = Math.max(0, new Date(date) - Date.now());
      setT({
        d: Math.floor(ms / 86400000),
        h: Math.floor(ms % 86400000 / 3600000),
        m: Math.floor(ms % 3600000 / 60000),
        s: Math.floor(ms % 60000 / 1000)
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [date]);

  return (
    <div className="bot-countdown">
      {[['дней', t.d], ['часов', t.h], ['минут', t.m], ['секунд', t.s]].map(([u, v]) => (
        <div key={u} className="bot-cu">
          <span className="bot-cu-val" style={{ color: accent }}>{String(v).padStart(2, '0')}</span>
          <span className="bot-cu-label">{u}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Calendar ───────────────────────────────────────────────── */
function CalendarWidget({ date, accent, addBtn }) {
  if (!date) return null;
  const d = new Date(date), yr = d.getFullYear(), mo = d.getMonth(), day = d.getDate();
  const off = (new Date(yr, mo, 1).getDay() + 6) % 7;
  const dim = new Date(yr, mo + 1, 0).getDate();
  const prevDim = new Date(yr, mo, 0).getDate();
  const cells = [];
  for (let i = off - 1; i >= 0; i--) cells.push({ n: prevDim - i, cur: false });
  for (let i = 1; i <= dim; i++)    cells.push({ n: i, cur: true });
  while (cells.length < 42)         cells.push({ n: cells.length - off - dim + 1, cur: false });
  const MON = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  return (
    <div className="bot-calendar">
      <div className="bot-cal-header">
        <span style={{ color: accent, fontWeight: 600 }}>{MON[mo]}</span>
        <span className="bot-cal-year">{yr}</span>
      </div>
      <div className="bot-cal-grid">
        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(h => (
          <div key={h} className="bot-cal-head" style={{ color: accent }}>{h}</div>
        ))}
        {cells.map((c, i) => {
          const isWedding = c.cur && c.n === day;
          return (
            <div key={i}
              className={`bot-cal-cell${!c.cur ? ' bot-cal-muted' : ''}${isWedding ? ' bot-cal-wedding' : ''}`}
              style={isWedding ? { background: accent } : {}}
            >{c.n}</div>
          );
        })}
      </div>
      {addBtn}
    </div>
  );
}

/* ── RSVP Form ──────────────────────────────────────────────── */
function RSVPForm({ project, accent }) {
  const [status, setStatus] = useState('confirmed');
  const [name, setName] = useState('');
  const [survey, setSurvey] = useState({});
  const [message, setMessage] = useState('');
  const [done, setDone] = useState(false);
  const bc = project.block_content || {};

  if (done) return (
    <div className="bot-rsvp-done" style={{ borderColor: accent }}>
      <div className="bot-rsvp-done-icon">
        <SmallLeaf />
      </div>
      <strong style={{ color: accent }}>Спасибо!</strong>
      <p>{status === 'confirmed' ? 'Ждём вас с нетерпением!' : 'Жаль, что не сможете прийти.'}</p>
    </div>
  );

  return (
    <form className="bot-rsvp-form" onSubmit={e => { e.preventDefault(); setDone(true); }}>
      <div className="bot-rsvp-options">
        {[{ v: 'confirmed', l: bc.rsvp?.yesText || 'Приду!' }, { v: 'declined', l: bc.rsvp?.noText || 'Не смогу прийти' }].map(o => (
          <label key={o.v}
            className={`bot-rsvp-opt${status === o.v ? ' active' : ''}`}
            style={status === o.v ? { background: accent, color: '#fff', borderColor: accent } : {}}
          >
            <input type="radio" name="rsvp" value={o.v} checked={status === o.v}
              onChange={() => setStatus(o.v)} style={{ display: 'none' }} />
            {o.l}
          </label>
        ))}
      </div>

      <input className="bot-input" placeholder="Ваше имя"
        value={name} onChange={e => setName(e.target.value)} required />

      {project.blocks?.includes('survey') && status === 'confirmed' && project.survey?.map(q => (
        <div key={q.id} className="bot-survey-q">
          <p className="bot-survey-label">{q.question}</p>
          {q.type === 'text'
            ? <textarea className="bot-input" rows={2} value={survey[q.id] || ''}
                onChange={e => setSurvey(s => ({ ...s, [q.id]: e.target.value }))} />
            : q.type === 'multiple'
            ? <div className="bot-survey-opts">{(q.options || []).map(opt => {
                const selected = Array.isArray(survey[q.id]) ? survey[q.id].includes(opt) : false;
                return (
                  <label key={opt}
                    className={`bot-rsvp-opt bot-rsvp-opt-sm${selected ? ' active' : ''}`}
                    style={selected ? { background: accent, color: '#fff', borderColor: accent } : {}}
                  >
                    <input type="checkbox" name={q.id} value={opt} checked={selected}
                      onChange={e => {
                        const cur = Array.isArray(survey[q.id]) ? survey[q.id] : [];
                        setSurvey(s => ({ ...s, [q.id]: e.target.checked ? [...cur, opt] : cur.filter(v => v !== opt) }));
                      }} style={{ display: 'none' }} />
                    {opt}
                  </label>
                );
              })}</div>
            : <div className="bot-survey-opts">{(q.options || []).map(opt => (
                <label key={opt}
                  className={`bot-rsvp-opt bot-rsvp-opt-sm${survey[q.id] === opt ? ' active' : ''}`}
                  style={survey[q.id] === opt ? { background: accent, color: '#fff', borderColor: accent } : {}}
                >
                  <input type="radio" name={q.id} value={opt}
                    onChange={() => setSurvey(s => ({ ...s, [q.id]: opt }))} style={{ display: 'none' }} />
                  {opt}
                </label>
              ))}</div>
          }
        </div>
      ))}

      {project.blocks?.includes('message') && (
        <div>
          {bc.message?.fieldLabel && (
            <p style={{ fontSize: '0.82rem', color: '#6b7e6e', marginBottom: 4 }}>{bc.message.fieldLabel}</p>
          )}
          <textarea className="bot-input" rows={3}
            placeholder={bc.message?.text || 'Ваши пожелания молодым...'}
            value={message} onChange={e => setMessage(e.target.value)} />
        </div>
      )}
      <button type="submit" className="bot-btn" style={{ background: accent }}>
        {bc.rsvp?.btnText || 'Подтвердить'}
      </button>
    </form>
  );
}

/* ── Wishlist ───────────────────────────────────────────────── */
function Wishlist({ project, accent }) {
  const [reserved, setReserved] = useState({});
  const items = project.wishlist || [];
  if (!items.length) return null;
  return (
    <div className="bot-wishlist">
      {items.map(item => (
        <div key={item.id} className="bot-wish-item">
          <div className="bot-wish-main">
            <div className="bot-wish-name">{item.name}</div>
            {item.price && <div className="bot-wish-price" style={{ color: accent }}>{item.price} ₽</div>}
          </div>
          <div className="bot-wish-actions">
            {item.link && (
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="bot-btn-outline">
                Посмотреть
              </a>
            )}
            {reserved[item.id]
              ? <button type="button" className="bot-btn" style={{ background: '#6b7e6e' }}
                  onClick={() => setReserved(r => ({ ...r, [item.id]: false }))}>
                  Снять резерв
                </button>
              : <button type="button" className="bot-btn" style={{ background: accent }}
                  onClick={() => setReserved(r => ({ ...r, [item.id]: true }))}>
                  Зарезервировать
                </button>
            }
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Scroll-reveal hook ─────────────────────────────────────── */
function useReveal(pageRef, blockKey) {
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const SEL = '.bot-reveal, .bot-reveal-left, .bot-reveal-right';
    const inPreview = !!root.closest('.lp-template-inner, .lp-template-outer');
    if (inPreview) {
      root.querySelectorAll(SEL).forEach(el => el.classList.add('bot-in'));
      return;
    }
    const els = root.querySelectorAll(SEL);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('bot-in');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [pageRef, blockKey]);
}

/* ══════════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════════ */
export default function Botanica({ project, design, isPreview }) {
  const pageRef = useRef(null);
  const blocks = project.blocks || [];
  useReveal(pageRef, blocks.join(','));

  const bc     = project.block_content || {};
  const accent = project.palette?.primary || '#c5a55a';
  const dark   = project.palette?.secondary || '#1a2e1e';

  const fontTitle = project.fonts?.title || null;
  const fontBody  = project.fonts?.body  || null;
  const fontStyle = {
    ...(fontTitle ? { '--f-title': `'${fontTitle}', serif` } : {}),
    ...(fontBody  ? { '--f-body':  `'${fontBody}', sans-serif` } : {}),
    '--c-accent': accent,
    '--c-dark':   dark,
  };

  useEffect(() => {
    const families = [fontTitle, fontBody].filter(Boolean);
    if (!families.length) return;
    const id = 'bot-dynamic-fonts';
    let link = document.getElementById(id);
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    const query = families
      .map(f => `family=${encodeURIComponent(f)}:wght@300;400;600;700`)
      .join('&');
    link.href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  }, [fontTitle, fontBody]);

  const photos = project.cover_photos || (project.cover_photo ? [{ url: project.cover_photo }] : []);
  const p1 = photos[0];
  const invPhotos = project.invitation_photos || [];
  const dcPhotos = project.dresscode_photos || [];

  const bride = project.bride || 'Невеста';
  const groom = project.groom || 'Жених';
  const venues = (project.venues || []).filter(v => (v.name || "").trim() || (v.address || "").trim());

  const tomorrowISO = (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  })();
  const weddingDate = project.wedding_date || tomorrowISO;

  const fmtDate = raw => {
    if (!raw) return 'ДД · ММ · ГГГГ';
    const d = new Date(raw);
    const months = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const HAS_SCHEDULE  = blocks.includes('schedule');
  const HAS_MAP = blocks.includes('map') && ((p?.venues || project?.venues || []).some(v => v?.address?.trim()) || !!(p?.wedding_address || project?.wedding_address));
  const HAS_DRESSCODE = blocks.includes('palette');
  const HAS_COUNTDOWN = blocks.includes('countdown');
  const HAS_RSVP      = blocks.includes('rsvp');
  const HAS_WISHLIST  = blocks.includes('wishlist');
  const HAS_CALENDAR  = blocks.includes('calendar');

  const allItems  = project.schedule || [];
  const optional  = allItems.filter(i => i.optional);
  const mandatory = allItems.filter(i => !i.optional);
  const mainItems = optional.length > 0 ? mandatory : allItems;

  return (
    <div className="bot-page" ref={pageRef} style={fontStyle}>

      {/* ══ HERO ═════════════════════════════════════════════════ */}
      <section className="bot-hero">
        {/* Background */}
        {p1
          ? <img className="bot-hero-bg" src={src(p1.url)} alt="" style={{ filter: flt(p1), objectPosition: objPos(p1) }} />
          : <div className="bot-hero-bg bot-hero-gradient" />
        }
        <div className="bot-hero-overlay" />

        {/* Leaf corner decorations */}
        <LeafCornerTL />
        <LeafCornerBR />

        {/* Hero text card */}
        <div className="bot-hero-card bot-hero-in">
          <div className="bot-hero-date-pill" style={{ background: accent }}>
            {fmtDate(weddingDate)}
          </div>
          <h1 className="bot-hero-names">
            <span className="bot-hero-bride">{bride}</span>
            <span className="bot-hero-amp">&amp;</span>
            <span className="bot-hero-groom">{groom}</span>
          </h1>
          <p className="bot-hero-slogan">
            {bc.hero?.subtitle || project.wedding_slogan || 'Вы приглашены на нашу свадьбу'}
          </p>
          {HAS_RSVP && (
            <a href="#rsvp" className="bot-btn bot-hero-btn" style={{ background: accent }}>
              Заполнить анкету
            </a>
          )}
        </div>
      </section>

      {/* ══ INVITATION ══════════════════════════════════════════ */}
      <section className="bot-section bot-section--invitation" id="invitation">
        <LeafDivider />
        <div className="bot-section-inner bot-two-col">
          <div className="bot-reveal-left">
            <h2 className="bot-section-title"
              dangerouslySetInnerHTML={{ __html: bc.message?.title || 'Дорогие гости' }} />
            <div className="bot-body-text">
              {bc.message?.text
                ? <div dangerouslySetInnerHTML={{ __html: bc.message.text }} />
                : (
                  <>
                    <p>С огромной радостью и волнением хотим <strong>пригласить вас</strong> разделить с нами самый важный день — день нашего <strong>бракосочетания</strong>.</p>
                    <p>Ваше присутствие сделает этот праздник по-настоящему незабываемым.</p>
                  </>
                )
              }
            </div>
            {HAS_RSVP && (
              <a href="#rsvp" className="bot-btn" style={{ background: accent, marginTop: '20px', display: 'inline-flex' }}>
                {bc.rsvp?.btnText || 'Заполнить анкету'}
              </a>
            )}
          </div>

          <div className="bot-invitation-right bot-reveal">
            {/* Photo gallery */}
            {invPhotos.length > 0 ? (
              <div className="bot-inv-photos">
                {invPhotos.slice(0, 3).map((ph, i) => (
                  <div key={i} className={`bot-inv-photo bot-inv-photo--${i}`}>
                    <img src={src(ph.url)} alt="" style={{ filter: flt(ph), objectPosition: objPos(ph) }} />
                  </div>
                ))}
              </div>
            ) : p1 ? (
              <div className="bot-inv-photos">
                <div className="bot-inv-photo bot-inv-photo--0">
                  <img src={src(p1.url)} alt="" style={{ filter: flt(p1), objectPosition: objPos(p1) }} />
                </div>
              </div>
            ) : (
              <div className="bot-inv-placeholder">
                <SmallLeaf />
                <span>Ваши фото появятся здесь</span>
              </div>
            )}

            {/* Calendar */}
            <CalendarWidget
              date={weddingDate}
              accent={accent}
              addBtn={HAS_CALENDAR && (() => {
                const d = new Date(weddingDate);
                const pad = n => String(n).padStart(2, '0');
                const ymd = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
                const title = encodeURIComponent(`Свадьба ${project.bride || ''} & ${project.groom || ''}`);
                const href = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${ymd}/${ymd}`;
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer"
                    className="bot-cal-add-btn" style={{ borderColor: accent, color: accent }}>
                    {bc.calendar?.title || 'Добавить в календарь'}
                  </a>
                );
              })()}
            />
          </div>
        </div>
      </section>

      {/* ══ COUNTDOWN ═══════════════════════════════════════════ */}
      {HAS_COUNTDOWN && (
        <section className="bot-section bot-section--alt" id="countdown">
          <LeafDivider />
          <div className="bot-section-inner">
            <div className="bot-two-col">
              <div className="bot-reveal-left">
                <h2 className="bot-section-title"
                  dangerouslySetInnerHTML={{ __html: bc.countdown?.title || 'До свадьбы осталось' }} />
                <BlockSubtitle text={bc.countdown?.subtitle} className="bot-body-text" style={{ color: 'var(--c-muted)' }} />
              </div>
              <div className="bot-reveal" style={{ '--d': '.15s' }}>
                <Countdown date={weddingDate} accent={accent} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ══ SCHEDULE ════════════════════════════════════════════ */}
      {HAS_SCHEDULE && (
        <section className="bot-section" id="schedule">
          <LeafDivider />
          <div className="bot-section-inner">
            <h2 className="bot-section-title bot-reveal"
              dangerouslySetInnerHTML={{ __html: bc.schedule?.title || 'Программа дня' }} />
            <BlockSubtitle text={bc.schedule?.subtitle} className="bot-body-text bot-reveal" style={{ color: 'var(--c-muted)', marginBottom: '32px' }} />
            <div className="bot-timeline">
              {allItems.length > 0 ? (
                <>
                  {optional.length > 0 && (
                    <>
                      <div className="bot-timeline-group-label">Необязательная часть</div>
                      {optional.map((item, i) => (
                        <div className="bot-timeline-item bot-reveal" key={`opt-${i}`} style={{ '--d': `${i * 0.1}s` }}>
                          <div className="bot-tl-time" style={{ color: accent }}>{item.time}</div>
                          <div className="bot-tl-dot" style={{ borderColor: accent }}>
                            <div style={{ background: accent }} />
                          </div>
                          <div className="bot-tl-info">
                            <div className="bot-tl-event">{item.event}</div>
                            {item.description && <div className="bot-tl-desc">{item.description}</div>}
                          </div>
                        </div>
                      ))}
                      <div className="bot-timeline-group-label">Основная программа</div>
                    </>
                  )}
                  {mainItems.map((item, i) => (
                    <div className="bot-timeline-item bot-reveal" key={`main-${i}`} style={{ '--d': `${i * 0.1}s` }}>
                      <div className="bot-tl-time" style={{ color: accent }}>{item.time}</div>
                      <div className="bot-tl-dot" style={{ borderColor: accent }}>
                        <div style={{ background: accent }} />
                      </div>
                      <div className="bot-tl-info">
                        <div className="bot-tl-event">{item.event}</div>
                        {item.description && <div className="bot-tl-desc">{item.description}</div>}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                [['14:00', 'Сбор гостей'], ['15:00', 'Церемония'], ['16:30', 'Фотосессия'], ['18:00', 'Банкет']].map(([t, e], i) => (
                  <div className="bot-timeline-item bot-reveal" key={i} style={{ opacity: 0.4 }}>
                    <div className="bot-tl-time" style={{ color: accent + '80' }}>{t}</div>
                    <div className="bot-tl-dot" style={{ borderColor: accent + '60' }}>
                      <div style={{ background: accent + '60' }} />
                    </div>
                    <div className="bot-tl-info"><div className="bot-tl-event">{e}</div></div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      )}

      {/* ══ MAP ═════════════════════════════════════════════════ */}
      {HAS_MAP && (
        <section className="bot-section bot-section--alt" id="map">
          <LeafDivider />
          <div className="bot-section-inner">
            <h2 className="bot-section-title bot-reveal"
              dangerouslySetInnerHTML={{ __html: bc.map?.title || 'Место торжества' }} />
            <BlockSubtitle text={bc.map?.subtitle} className="bot-body-text bot-reveal" style={{ color: 'var(--c-muted)', marginBottom: '24px' }} />
            <div className="bot-venues">
              {venues.length > 0 ? venues.map((v, i) => (
                <div key={i} className="bot-venue-row bot-reveal" style={{ '--d': `${i * 0.12}s` }}>
                  <div className="bot-venue-icon" style={{ color: accent }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div className="bot-venue-info">
                    {v.label && <span className="bot-venue-label" style={{ color: accent }}>{v.label}</span>}
                    {v.name && <span className="bot-venue-name">{v.name} — </span>}
                    <span className="bot-venue-addr">{v.address}</span>
                  </div>
                  <a href={`https://yandex.ru/maps/?text=${encodeURIComponent(v.address)}&z=16`}
                    target="_blank" rel="noopener noreferrer" className="bot-btn-outline">
                    Маршрут
                  </a>
                </div>
              )) : project.wedding_address ? (
                <div className="bot-venue-row">
                  <div className="bot-venue-icon" style={{ color: accent }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <span className="bot-venue-addr">{project.wedding_address}</span>
                </div>
              ) : (
                <div className="bot-venue-row" style={{ color: 'var(--c-muted)', fontSize: '0.9rem' }}>
                  Адрес будет добавлен позднее
                </div>
              )}
            </div>
            {(project.map_url || (project.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
            <div className="bot-map-frame bot-reveal" style={{ '--d': '.2s' }}>
              {(() => {
                if (project.map_url) {
                  return <MapFrame region={project.region} title="Карта" src={project.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                }
                const withCoords = venues.filter(v => v.lat && v.lon);
                if (withCoords.length > 0) {
                  const clat = withCoords.reduce((s, v) => s + v.lat, 0) / withCoords.length;
                  const clon = withCoords.reduce((s, v) => s + v.lon, 0) / withCoords.length;
                  const pts  = withCoords.map(v => `${v.lon},${v.lat},pm2rdm`).join('~');
                  const zoom = withCoords.length > 1 ? 12 : 16;
                  const mapSrc = `https://yandex.ru/map-widget/v1/?ll=${clon},${clat}&z=${zoom}&pt=${pts}&lang=ru_RU`;
                  return <MapFrame region={project.region} title="Карта" src={mapSrc} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                }
                const addr = venues.length > 0
                  ? venues.map(v => v.address).filter(Boolean).join(' — ')
                  : (project.wedding_address || '');
                if (!addr) {
                  return <MapFrame region={project.region} title="Карта" src="https://yandex.ru/map-widget/v1/?ll=37.617617,55.755811&z=12&lang=ru_RU" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                }
                const mapSrc = venues.length > 1
                  ? `https://yandex.ru/map-widget/v1/?rtext=${venues.map(v => encodeURIComponent(v.address)).join('~')}&rtt=auto&lang=ru_RU`
                  : `https://yandex.ru/map-widget/v1/?text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                return <MapFrame region={project.region} title="Карта" src={mapSrc} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
              })()}
            </div>
            )}
          </div>
        </section>
      )}

      {/* ══ DRESSCODE ═══════════════════════════════════════════ */}
      {HAS_DRESSCODE && (
        <section className="bot-section" id="dresscode">
          <LeafDivider />
          <div className="bot-section-inner">
            <div className="bot-two-col">
              <div className="bot-reveal-left">
                <h2 className="bot-section-title"
                  dangerouslySetInnerHTML={{ __html: bc.palette?.title || 'Дресс-код' }} />
                <BlockSubtitle text={bc.palette?.subtitle} className="bot-body-text" tag="div" />
                <div className="bot-swatches">
                  {(project.dresscode_palette || []).map((sw, i) => {
                    const c = sw.color || '#ffffff';
                    const hex = c.replace('#', '');
                    const r = parseInt(hex.slice(0, 2), 16) || 255;
                    const g = parseInt(hex.slice(2, 4), 16) || 255;
                    const b = parseInt(hex.slice(4, 6), 16) || 255;
                    const isLight = (r * 299 + g * 587 + b * 114) / 1000 > 160;
                    return (
                      <div key={i} className="bot-swatch bot-reveal"
                        style={{ background: c, border: isLight ? '1px solid rgba(0,0,0,0.1)' : 'none', '--d': `${i * 0.08}s` }}>
                        {sw.label && <span style={{ color: isLight ? '#1a2e1e' : '#fff' }}>{sw.label}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
              {dcPhotos.length > 0 && (
                <div className="bot-dc-photos bot-reveal" style={{ '--d': '.2s' }}>
                  {dcPhotos.slice(0, 3).map((p, i) => (
                    <div key={i} className="bot-dc-photo">
                      <img src={src(p.url)} alt="" style={{ filter: flt(p), objectPosition: objPos(p) }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ══ RSVP + WISHLIST ═════════════════════════════════════ */}
      {(HAS_RSVP || HAS_WISHLIST) && (
        <section className="bot-section bot-section--alt" id="rsvp">
          <LeafDivider />
          <div className="bot-section-inner">
            {HAS_RSVP && (
              <div className="bot-card bot-reveal">
                <h2 className="bot-section-title"
                  dangerouslySetInnerHTML={{ __html: bc.rsvp?.title || 'Анкета гостя' }} />
                <BlockSubtitle text={bc.rsvp?.subtitle} className="bot-body-text" style={{ marginBottom: '24px' }} />
                {bc.rsvp?.deadline && (
                  <p className="bot-body-text" style={{ marginBottom: '20px', color: accent }}>
                    Ответьте до: <strong>{new Date(bc.rsvp.deadline).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                  </p>
                )}
                <RSVPForm project={project} accent={accent} />
              </div>
            )}
            {HAS_WISHLIST && (
              <div className="bot-card bot-reveal" style={{ '--d': '.15s' }}>
                <h2 className="bot-section-title"
                  dangerouslySetInnerHTML={{ __html: bc.wishlist?.title || 'Список желаний' }} />
                <BlockSubtitle text={bc.wishlist?.subtitle} className="bot-body-text" style={{ marginBottom: '24px' }} />
                <Wishlist project={project} accent={accent} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══ WATERMARK ═══════════════════════════════════════════ */}
      {(!project.payment_plan || project.payment_plan === 'free') && (
        <div style={{
          position: 'fixed', bottom: 16, right: 16, zIndex: 9999,
          background: 'rgba(26,46,30,0.9)', color: '#f8f5ef',
          borderRadius: '8px', padding: '8px 14px',
          fontSize: '0.75rem', backdropFilter: 'blur(8px)',
          border: '1px solid rgba(197,165,90,0.3)',
        }}>
          Создано на <strong style={{ color: '#c5a55a' }}>Amorika</strong>
        </div>
      )}

      {/* ══ FOOTER ══════════════════════════════════════════════ */}
      <footer className="bot-footer">
        <LeafCornerTL />
        <div className="bot-footer-inner">
          <div className="bot-footer-names" style={{ color: accent }}>
            {bride} &amp; {groom}
          </div>
          <div className="bot-footer-date">{fmtDate(weddingDate)}</div>
          <div className="bot-footer-slogan">
            {project.wedding_slogan || 'Одна любовь, одна жизнь, один особенный день'}
          </div>
          <div className="bot-footer-brand">
            Создано на <a href="/" style={{ color: accent, textDecoration: 'none' }}>Amorika</a>
          </div>
        </div>
      </footer>

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={isPreview} />
      )}

    </div>
  );
}
